import React, { useState, useEffect } from 'react'
import { X, ChevronDown } from 'lucide-react'
import { toast } from 'react-hot-toast'
import employeeAllowanceService from '../../../services/employeeAllowanceService'
import employeeService from '../../../services/employeeService'
import branchService from '../../../services/branchService'
import { jobService } from '../../../services/jobService'

const CreateEmployeeAllowanceModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    employee: '',
    houseAllowance: '',
    transportAllowance: '',
    foodAllowance: '',
    healthAllowance: ''
  })
  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  const [jobs, setJobs] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch employees when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchEmployees()
      resetForm()
    }
  }, [isOpen])

  const fetchEmployees = async () => {
    setIsLoading(true)
    try {
      // Fetch all required data in parallel
      const [employeesResponse, branchesResponse, jobsResponse] = await Promise.all([
        employeeService.getAllEmployees(),
        branchService.getAllBranches(),
        jobService.getAllJobs()
      ])

      if (employeesResponse.success && branchesResponse.success && jobsResponse.success) {
        const employeesData = employeesResponse.data.employees || []
        const branchesData = branchesResponse.data.branches || []
        const jobsData = jobsResponse.data.jobs || []
        
        setBranches(branchesData)
        setJobs(jobsData)

        // Enhance employees with branch and job names
        const enhancedEmployees = employeesData.filter(emp => emp.isActive).map(employee => {
          const branch = branchesData.find(b => b._id === employee.branch)
          const job = jobsData.find(j => j._id === employee.job)
          
          return {
            ...employee,
            branchName: branch?.name || 'N/A',
            jobTitle: job?.name || job?.title || 'N/A'
          }
        })
        
        setEmployees(enhancedEmployees)
      }
    } catch (error) {
      console.error('Error fetching employees:', error)
      toast.error('Failed to fetch employees')
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      employee: '',
      houseAllowance: '',
      transportAllowance: '',
      foodAllowance: '',
      healthAllowance: ''
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validateForm = () => {
    if (!formData.employee) {
      toast.error('Please select an employee')
      return false
    }
    
    const allowanceFields = ['houseAllowance', 'transportAllowance', 'foodAllowance', 'healthAllowance']
    for (const field of allowanceFields) {
      if (!formData[field] || parseFloat(formData[field]) < 0) {
        toast.error(`Please enter a valid ${field.replace('Allowance', ' allowance')}`)
        return false
      }
    }
    
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setIsSubmitting(true)
    
    try {
      const submitData = {
        employee: formData.employee,
        houseAllowance: parseFloat(formData.houseAllowance),
        transportAllowance: parseFloat(formData.transportAllowance),
        foodAllowance: parseFloat(formData.foodAllowance),
        healthAllowance: parseFloat(formData.healthAllowance)
      }
      
      const response = await employeeAllowanceService.createEmployeeAllowance(submitData)
      
      if (response.success) {
        toast.success(response.message || 'Employee allowance created successfully')
        onSuccess()
        onClose()
      } else {
        toast.error(response.message || 'Failed to create employee allowance')
      }
    } catch (error) {
      console.error('Error creating employee allowance:', error)
      toast.error('Failed to create employee allowance')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Assign Allowances</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee *
              </label>
              <div className="relative">
                <select
                  name="employee"
                  value={formData.employee}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white"
                  required
                  disabled={isLoading}
                >
                  <option value="">Select Employee</option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.fullName} 
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              </div>
            </div>

            {/* Allowance Fields - Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* House Allowance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  House Allowance *
                </label>
                <input
                  type="number"
                  name="houseAllowance"
                  value={formData.houseAllowance}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              {/* Transport Allowance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transport Allowance *
                </label>
                <input
                  type="number"
                  name="transportAllowance"
                  value={formData.transportAllowance}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              {/* Food Allowance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Food Allowance *
                </label>
                <input
                  type="number"
                  name="foodAllowance"
                  value={formData.foodAllowance}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              {/* Health Allowance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Health Allowance *
                </label>
                <input
                  type="number"
                  name="healthAllowance"
                  value={formData.healthAllowance}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer - Fixed */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateEmployeeAllowanceModal
